import { useState, useEffect, useRef } from "react";
import createPrivateAdminPanelInstance from "../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../api/ApiRoutes";
import { setStoredAdminTimezone } from "../utils/dateService";
import { setStoredAdminReadOnly } from "../utils/adminReadOnlyStore";
import { ADMIN_TYPES } from "../utils/Constants";
import {
  getStoredGuestLanguage,
  setStoredAdminLanguage,
} from "../utils/i18n/languagePreference";
import { patchStaffMePreferences } from "../queries/useUpdateStaffPreferences";

const formatAdminRoleLabel = (roleValue) => {
  if (!roleValue) return "";

  return String(roleValue)
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getAdminRoleLabel = (data) => {
  const roleValue =
    data?.role_name ??
    data?.role?.name ??
    data?.role?.label ??
    data?.role ??
    data?.group_name ??
    data?.admin_type ??
    data?.role_type ??
    "";

  return formatAdminRoleLabel(roleValue);
};

/**
 * Maps auth/me response to an admin type the Sider understands.
 * - Legacy: group_name (or admin_type) may be "super_admin" / "super-admin-v2", etc.
 * - New roles: BE may return no group_name; if we have permissions or valid staff data, treat as customer-support so tabs show.
 */
function getAdminTypeForSider(data) {
  const raw =
    data?.group_name ??
    data?.admin_type ??
    data?.role_type ??
    "";

  const normalized = String(raw).trim().toLowerCase().replace(/[_\s]+/g, "-");
  if (
    normalized === "super-admin-v2" ||
    normalized === "superadmin" ||
    normalized === "super-admin"
  ) {
    return ADMIN_TYPES.SUPER_ADMIN;
  }
  if (normalized === "customer-support") {
    return ADMIN_TYPES.CUSTOMER_SUPPORT;
  }
  if (normalized === "compliance-agent") {
    return ADMIN_TYPES.COMPLIANCE_AGENT;
  }

  // New Staff and Permissions roles: no legacy group_name; BE returns correct auth/me with permissions or staff identity
  const permissions =
    data?.permissions ??
    data?.user_permissions?.permissions ??
    data?.user_permissions ??
    [];
  const hasPermissions = Array.isArray(permissions) && permissions.length > 0;
  const hasStaffIdentity =
    data?.id != null ||
    data?.staff_id != null ||
    data?.admin_id != null ||
    data?.username != null;

  if (hasPermissions || hasStaffIdentity) {
    return ADMIN_TYPES.CUSTOMER_SUPPORT;
  }

  return "";
}

/**
 * Hook for fetching and managing admin authentication data.
 * Fetches admin permissions, type, and timezone when token changes.
 *
 * @param {Object} token - The current authentication token object
 * @param {Date} lastLogInTimestamp - Timestamp of last login (triggers re-fetch)
 * @returns {Object} Admin auth state
 */
const useAdminAuth = (token, lastLogInTimestamp) => {
  const [adminType, setAdminType] = useState("");
  const [adminPermissions, setAdminPermissions] = useState(null);
  const [adminActions, setAdminActions] = useState(null);
  const [adminReadOnly, setAdminReadOnly] = useState(false);
  const [adminRegulatorManager, setAdminRegulatorManager] = useState(false);
  const [adminDataLoaded, setAdminDataLoaded] = useState(false);
  const [adminTimezone, setAdminTimezone] = useState(null);
  const [adminLanguage, setAdminLanguage] = useState(null);
  const [adminUsername, setAdminUsername] = useState(null);
  const [adminEmail, setAdminEmail] = useState(null);
  const [adminRoleLabel, setAdminRoleLabel] = useState("");
  const previousAccessTokenRef = useRef(null);

  // Use refs to track previous values for conditional logic
  const prevAdminType = useRef(adminType);
  const prevAdminPermissions = useRef(adminPermissions);
  const prevAdminActions = useRef(adminActions);

  useEffect(() => {
    const resetAdminState = () => {
      setAdminType("");
      setAdminPermissions([]);
      setAdminActions([]);
      // Writable on reset: a transient /me failure must not lock out a
      // legit admin (the BE fail-closes on every enforced surface anyway).
      setAdminReadOnly(false);
      setAdminRegulatorManager(false);
      setStoredAdminReadOnly(false);
      setAdminTimezone(null);
      setAdminLanguage(null);
      setAdminUsername(null);
      setAdminEmail(null);
      setAdminRoleLabel("");
      setStoredAdminTimezone(null);
      setStoredAdminLanguage(null);
      prevAdminType.current = "";
      prevAdminPermissions.current = [];
      prevAdminActions.current = [];
    };

    const fetchAdminData = async () => {
      const storedToken = JSON.parse(localStorage.getItem("token"));
      const currentAccessToken = storedToken?.AccessToken || token?.AccessToken || null;
      const tokenChanged = previousAccessTokenRef.current !== currentAccessToken;

      if (tokenChanged) {
        setAdminDataLoaded(false);
        // Clear old admin state while loading new identity details to avoid stale profile/timezone data.
        resetAdminState();
      }

      previousAccessTokenRef.current = currentAccessToken;
      
      if (!storedToken?.AccessToken) {
        setAdminDataLoaded(true);
        resetAdminState();
        return;
      }

      try {
        const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
          storedToken.AccessToken
        );
        const response = await PrivateAdminPanelInstance?.get(
          API_ROUTES?.GET_ADMINS_SELF_DATA
        );

        const data = response?.data?.data ?? response?.data ?? {};

        // Extract timezone from various possible response formats
        const timezoneFromResponse =
          data?.timezone ??
          data?.time_zone ??
          data?.admin_timezone ??
          data?.settings?.timezone ??
          null;

        setAdminTimezone(timezoneFromResponse);
        setStoredAdminTimezone(timezoneFromResponse);

        // Extract the language preference defensively (contract C1: the field
        // may be absent while the backend endpoint is still rolling out —
        // treat that as null, same multi-candidate style as timezone above).
        const languageFromResponse =
          data?.language ??
          data?.preferred_language ??
          data?.settings?.language ??
          null;

        // Login transition: a pending pre-auth guest choice wins over the
        // stored profile preference and is PATCHed to the profile
        // (contract C1, 404 → localStorage-only). The guest key is kept so the
        // same language is still selected after logout (login screen).
        let effectiveLanguage = languageFromResponse;
        const guestLanguage = getStoredGuestLanguage();
        if (guestLanguage) {
          effectiveLanguage = guestLanguage;
          try {
            await patchStaffMePreferences({ language: guestLanguage });
          } catch (error) {
            // Keep the choice locally even if the PATCH fails — the mirror
            // below still applies it; the profile just won't carry it yet.
            // eslint-disable-next-line no-console
            console.error(
              "Failed to persist guest language preference:",
              error
            );
          }
        }

        setAdminLanguage(effectiveLanguage);
        setStoredAdminLanguage(effectiveLanguage);

        setAdminUsername(data?.username || null);
        setAdminEmail(data?.email ?? data?.user_email ?? null);
        setAdminRoleLabel(getAdminRoleLabel(data));

        const resolvedAdminType = getAdminTypeForSider(data);
        setAdminType(resolvedAdminType);
        prevAdminType.current = resolvedAdminType;

        // Extract permissions from various possible response formats
        const permissionsFromResponse =
          data?.permissions ??
          data?.user_permissions?.permissions ??
          data?.user_permissions ??
          [];

        const normalizedPermissions = Array.isArray(permissionsFromResponse)
          ? permissionsFromResponse
          : [];

        setAdminPermissions(normalizedPermissions);
        prevAdminPermissions.current = normalizedPermissions;

        // Extract actions from various possible response formats
        const actionsFromResponse =
          data?.actions ??
          data?.user_actions?.actions ??
          data?.user_actions ??
          [];

        const normalizedActions = Array.isArray(actionsFromResponse)
          ? actionsFromResponse
          : [];

        setAdminActions(normalizedActions);
        prevAdminActions.current = normalizedActions;

        const readOnlyFromResponse = data?.read_only_access === true;
        setAdminReadOnly(readOnlyFromResponse);
        setStoredAdminReadOnly(readOnlyFromResponse);
        setAdminRegulatorManager(data?.regulator_manager === true);

        setAdminDataLoaded(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error retrieving admin permissions:", error);

        resetAdminState();
        setAdminDataLoaded(true);
      }
    };

    fetchAdminData();
  }, [token, lastLogInTimestamp]);

  let localStorageAccessToken = null;
  try {
    localStorageAccessToken =
      JSON.parse(localStorage.getItem("token") || "null")?.AccessToken ||
      null;
  } catch {
    // Corrupted localStorage payload — treat as no token. The effect
    // below will also see this and route to the unauthenticated path.
    localStorageAccessToken = null;
  }
  const adminDataMatchesCurrentToken =
    localStorageAccessToken === previousAccessTokenRef.current;

  return {
    adminType,
    adminPermissions,
    adminActions,
    adminReadOnly,
    adminRegulatorManager,
    adminDataLoaded: adminDataLoaded && adminDataMatchesCurrentToken,
    adminTimezone,
    adminLanguage,
    adminUsername,
    adminEmail,
    adminRoleLabel,
  };
};

export default useAdminAuth;
